<script setup lang="ts">
import type { FieldState } from '@/utils/field-state'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    ariaLabel?: string
    state?: FieldState | null
  }>(),
  {
    disabled: false,
    state: null,
  },
)

const model = defineModel<boolean | null>({ default: null })

const { t } = useI18n({ useScope: 'local' })

const answer = computed(() => {
  if (model.value === null || model.value === undefined) {
    return 'unanswered'
  }

  return model.value ? 'yes' : 'no'
})

const appearanceClass = computed(() => {
  if (props.state === 'missing') {
    return 'border-warning bg-warning-subtle border-dashed text-transparent'
  }

  if (props.state === 'reading') {
    return 'border-auto-line bg-auto-subtle text-transparent'
  }

  if (answer.value === 'yes') {
    return 'bg-action border-action text-white'
  }

  return answer.value === 'no' ? 'border-line bg-surface-3 text-ink-2' : 'border-line bg-surface text-transparent'
})

function toggle(): void {
  model.value = answer.value === 'unanswered' ? true : answer.value === 'yes' ? false : null
}
</script>

<template>
  <button
    type="button"
    role="checkbox"
    :aria-checked="answer === 'unanswered' ? 'mixed' : answer === 'yes'"
    :aria-label="ariaLabel"
    :title="t(answer)"
    :disabled="disabled"
    class="focus-visible:ring-action-700/20 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border transition-all focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    :class="appearanceClass"
    @click="toggle">
    <i :class="answer === 'no' ? 'pi pi-times' : 'pi pi-check'" class="text-sm leading-none" aria-hidden="true" />
  </button>
</template>

<i18n lang="json">
{
  "de": {
    "yes": "Ja",
    "no": "Nein",
    "unanswered": "Keine Angabe"
  },
  "en": {
    "yes": "Yes",
    "no": "No",
    "unanswered": "Not specified"
  }
}
</i18n>
